import { RouteNames } from "@/router/routes.ts";
import { App } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { defineStore } from "pinia";
import { Monitoring } from "@movinglayers/monitoring/frontend";
import { attachEventListenersOnlyForAndroid } from "@movinglayers/platforms";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStorageStore } from "./storage.ts";
import { useSyncStore } from "./sync.ts";
import { useUserStore } from "./user.ts";
import { useAlertsStore } from "./alerts.ts";

const checkAppStateInterval = 60000; // 1 minute

export const useAppLifecycleStore = defineStore("app-lifecycle", () => {

    const isAppFocused = ref(true);
    const isAppInBackground = ref(false);
    const isAppOnline = ref(false);

    const storageStore = useStorageStore();
    const userStore = useUserStore();
    const syncStore = useSyncStore();
    const alertsStore = useAlertsStore();
    const router = useRouter();

    const canDoServerRequests = computed(() => {
        return isAppFocused.value && isAppOnline.value && !isAppInBackground.value;
    });

    const startAppStateCheckInterval = () => {
        // try to cleanup unwanted/inconsistent states in the app in this function, for now only sync interval
        setInterval(() => {
            if (userStore.user && canDoServerRequests.value && !syncStore.isSyncing) {
                syncStore.startSyncInterval();
            }
        }, checkAppStateInterval);
    };

    const afterAuthenticating = async (happeningAfterLoggedIn = false) => {
        if (happeningAfterLoggedIn) {
            // user info set by login response
            syncStore.startSyncInterval();
            router.push({ name: RouteNames.MAP });
        } else {
            try {
                await userStore.fetchMe();
                syncStore.startSyncInterval();
            } catch (error: any) {
                Monitoring.chainError("Error after authenticating", error);
                const alert = await alertsStore.getErrorWhileAuthenticatingAlert(() => {
                    userStore.logoutUser();
                });
                alert.present();
            }
        };
    };

    const startJobs = () => {
        startAppStateCheckInterval();
    };

    const backgroundInitialize = () => {
        nextTick(() => {
            App.addListener("pause", () => isAppInBackground.value = true);
            App.addListener("resume", () => isAppInBackground.value = false);
            startJobs();
        }).catch((error: any) => {
            Monitoring.chainError("Error while initializing app in background", error);
        });
    };

    //TODO: assumption for now: app is online when it starts
    const initialize = async () => {
        let isAuth = false;
        try {
            await attachEventListeners();
            await storageStore.initStorage();
            isAuth = await userStore.checkAuthenticated();

        } catch (error: any) {
            Monitoring.chainError("Error while initializing app", error);
        }
        if (isAuth) {
            afterAuthenticating();
        }
        backgroundInitialize();
    };

    const refresh = () => {
        syncStore.startSyncInterval();
    };

    const reset = () => {
        syncStore.stopSyncInterval();
        storageStore?.clearHobbyistStorage();
    };

    const attachEventListeners = () => {
        const appStatePromise = App.getState().then(({ isActive }) => {
            isAppFocused.value = isActive;

            App.addListener("appStateChange", ({ isActive }) => {
                isAppFocused.value = isActive;
            });
        });

        const networkStatusPromise = Network.getStatus().then(({ connected }) => {
            isAppOnline.value = connected;

            Network.addListener("networkStatusChange", ({ connected }) => {
                isAppOnline.value = connected;
            });
        });

        const safeAreaPromise = attachEventListenersOnlyForAndroid();

        return Promise.all([appStatePromise, networkStatusPromise, safeAreaPromise]);
    };

    watch(canDoServerRequests, (canDo) => {
        if (canDo && !syncStore.isSyncing && !!userStore.user) {
            syncStore.startSyncInterval();
        }
    });

    return {
        canDoServerRequests,
        initialize,
        afterAuthenticating,
        backgroundInitialize,
        refresh,
        reset,
        startJobs,
        attachEventListeners,
        isAppOnline
    };
});